import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Fingerprint } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useImpronte } from "../lib/useImpronte";

/**
 * TIME Watch → Utility → Impronta
 *
 * Una sola impronta al giorno. L'utente sceglie la categoria in cui sente di
 * aver lasciato maggiormente il proprio segno oggi. Percentuali calcolate su
 * tutto lo storico. Animazione delle barre da 0% al valore reale.
 *
 * Persistenza:
 *   • Utente autenticato → MongoDB via /api/impronte
 *   • Anonimo → localStorage "time-watch:impronte" (fallback)
 */

const CATEGORIES = [
  {
    slug: "lavoro",
    icon: "/icons/impronta/lavoro.png",
    label: "Lavoro",
    color: "#C9A27C", // marrone chiaro
  },
  {
    slug: "famiglia",
    icon: "/icons/impronta/famiglia.png",
    label: "Famiglia",
    color: "#D9D0F0", // pervinca / lavanda chiarissimo
  },
  {
    slug: "amici",
    icon: "/icons/impronta/amici.png",
    label: "Amici",
    color: "#C8CDD3", // grigio chiaro
  },
  {
    slug: "passioni",
    icon: "/icons/impronta/passioni.png",
    label: "Passioni",
    color: "#F2D24A", // giallo
  },
  {
    slug: "animali",
    icon: "/icons/impronta/animali.png",
    label: "Animali",
    color: "#F0A24A", // arancione
  },
];

const todayIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function TimeWatchImprontaPage() {
  const { items: history, addImpronta, loaded } = useImpronte();
  const [pending, setPending] = useState(null); // {slug} in attesa di conferma
  const [pendingNote, setPendingNote] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [barsMounted, setBarsMounted] = useState(false);
  const [error, setError] = useState("");

  const today = todayIso();
  const todayRecord = history.find((r) => r.date === today);

  // Calcolo percentuali sul totale + counts.
  const stats = useMemo(() => {
    const counts = Object.fromEntries(CATEGORIES.map((c) => [c.slug, 0]));
    history.forEach((r) => {
      if (counts[r.category] !== undefined) counts[r.category] += 1;
    });
    const total = history.length;
    return CATEGORIES.map((c) => ({
      ...c,
      count: counts[c.slug],
      percent: total > 0 ? Math.round((counts[c.slug] / total) * 100) : 0,
    }));
  }, [history]);

  // Animazione barre: monta a 0, dopo 60ms passa al valore reale (transizione 900ms).
  useEffect(() => {
    setBarsMounted(false);
    const t = setTimeout(() => setBarsMounted(true), 60);
    return () => clearTimeout(t);
  }, [history.length]);

  const triggerHaptic = () => {
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      try {
        navigator.vibrate(20);
      } catch {
        /* ignore */
      }
    }
  };

  const handlePrintTap = (slug) => {
    if (todayRecord) return;
    triggerHaptic();
    setPendingNote("");
    setPending({ slug });
  };

  const confirmImpronta = async () => {
    if (!pending) return;
    setError("");
    try {
      await addImpronta({
        date: today,
        category: pending.slug,
        note: pendingNote,
      });
      setPending(null);
      setPendingNote("");
      setJustSaved(true);
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate([25, 40, 25]);
        } catch {
          /* ignore */
        }
      }
      setTimeout(() => setJustSaved(false), 3500);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Errore nel salvataggio");
      setPending(null);
      setPendingNote("");
    }
  };

  const cancelImpronta = () => {
    setPending(null);
    setPendingNote("");
  };

  const chosenCategory = todayRecord
    ? CATEGORIES.find((c) => c.slug === todayRecord.category)
    : null;

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-3xl mx-auto">
      <Link
        to="/innovazione/time-watch/utility"
        data-testid="time-watch-impronta-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Utility
      </Link>

      <PageHeader
        eyebrow="Utility"
        title="IMPRONTA"
        description={
          <>
            <span className="text-emerald-300">TIME</span>
            <br />
            In quale categoria senti di aver lasciato maggiormente il tuo
            segno oggi?
          </>
        }
        testId="time-watch-impronta-header"
      />

      {justSaved && (
        <div
          data-testid="impronta-saved-toast"
          className="mb-6 glass-card p-4 border-emerald-400/50 text-center text-emerald-200 text-sm font-light animate-pulse"
        >
          La tua Impronta di oggi è stata registrata.
        </div>
      )}

      {error && (
        <div
          data-testid="impronta-error"
          className="mb-6 glass-card p-4 border-red-400/50 text-center text-red-300 text-sm font-light"
        >
          {error}
        </div>
      )}

      <div data-testid="impronta-list" className="space-y-4 sm:space-y-5">
        {stats.map((c) => {
          const isChosen = chosenCategory && chosenCategory.slug === c.slug;
          const isLocked = !!todayRecord;
          const barWidth = barsMounted ? `${c.percent}%` : "0%";
          return (
            <div
              key={c.slug}
              data-testid={`impronta-row-${c.slug}`}
              className="flex items-center gap-4 sm:gap-5"
            >
              {/* Percentuale */}
              <div
                data-testid={`impronta-percent-${c.slug}`}
                className="w-14 sm:w-16 text-right text-white/70 font-light tabular-nums text-lg sm:text-xl"
              >
                {c.percent}%
              </div>

              {/* Barra + emoji + label */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2 text-sm sm:text-base font-light text-white/85">
                  <span className="flex items-center gap-2.5">
                    <img
                      src={c.icon}
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                      className="w-9 h-9 sm:w-10 sm:h-10 object-contain select-none shrink-0"
                    />
                    <span className="flex flex-col leading-tight">
                      <span>{c.label}</span>
                      <span
                        data-testid={`impronta-count-${c.slug}`}
                        className="text-[10px] tracking-[0.24em] uppercase text-white/40"
                      >
                        {c.count} {c.count === 1 ? "volta" : "volte"}
                      </span>
                    </span>
                  </span>
                </div>
                <div className="relative h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div
                    data-testid={`impronta-bar-${c.slug}`}
                    style={{
                      width: barWidth,
                      backgroundColor: c.color,
                      transition:
                        "width 900ms cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                    className="h-full rounded-full"
                  />
                </div>
              </div>

              {/* Impronta digitale */}
              <FingerprintButton
                color={c.color}
                filled={isChosen}
                disabled={isLocked && !isChosen}
                onTap={() => handlePrintTap(c.slug)}
                testId={`impronta-print-${c.slug}`}
              />
            </div>
          );
        })}
      </div>

      {todayRecord && (
        <div
          data-testid="impronta-today-locked"
          className="mt-10 text-center text-[11px] tracking-[0.32em] uppercase text-white/40"
        >
          Impronta di oggi registrata · Torna domani
        </div>
      )}

      {pending && (
        <ConfirmImpronta
          category={CATEGORIES.find((c) => c.slug === pending.slug)}
          note={pendingNote}
          onNoteChange={setPendingNote}
          onCancel={cancelImpronta}
          onConfirm={confirmImpronta}
        />
      )}
    </div>
  );
}

/**
 * FingerprintButton — icona impronta digitale minimalista.
 * Due Fingerprint sovrapposti: quello di base bianco/20, quello colorato
 * clippato dal basso verso l'alto per creare l'effetto "riempimento".
 */
function FingerprintButton({ color, filled, disabled, onTap, testId }) {
  const [pressAnim, setPressAnim] = useState(false);
  const [fillProgress, setFillProgress] = useState(0); // 0..1

  useEffect(() => {
    if (filled) setFillProgress(1);
    else setFillProgress(0);
  }, [filled]);

  const handleClick = () => {
    if (disabled) return;
    setPressAnim(true);
    // riempimento graduale ~600ms verso 0.85 (poi torna a 0 se annullato)
    let start = null;
    const dur = 600;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      setFillProgress(p * 0.85);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    setTimeout(() => setPressAnim(false), 420);
    onTap && onTap();
  };

  const inset = `${Math.max(0, (1 - fillProgress) * 100)}% 0 0 0`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      data-testid={testId}
      aria-label="Lascia la tua impronta"
      className={`relative w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-full transition ${
        disabled
          ? "opacity-30 cursor-not-allowed"
          : "hover:bg-white/5 active:scale-95"
      } ${pressAnim ? "scale-110" : "scale-100"}`}
      style={{ transitionDuration: "220ms" }}
    >
      {/* base outline */}
      <Fingerprint
        className="w-7 h-7 sm:w-8 sm:h-8 text-white/30"
        strokeWidth={1.3}
      />
      {/* fill overlay */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{
          clipPath: `inset(${inset})`,
          WebkitClipPath: `inset(${inset})`,
          transition: filled
            ? "clip-path 500ms ease-out, -webkit-clip-path 500ms ease-out"
            : undefined,
        }}
      >
        <Fingerprint
          className="w-7 h-7 sm:w-8 sm:h-8"
          strokeWidth={1.6}
          style={{ color }}
        />
      </div>
    </button>
  );
}

function ConfirmImpronta({ category, note, onNoteChange, onCancel, onConfirm }) {
  if (!category) return null;
  return (
    <div
      data-testid="impronta-confirm"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="glass-card w-full max-w-sm p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] tracking-[0.32em] uppercase text-white/40 mb-4">
          Hai scelto
        </div>
        <div
          className="text-3xl sm:text-4xl font-extralight text-white mb-6 flex items-center justify-center gap-3"
          style={{ color: category.color }}
        >
          <img
            src={category.icon}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="w-10 h-10 sm:w-11 sm:h-11 object-contain select-none"
          />
          <span>{category.label}</span>
        </div>
        <div className="text-white/70 font-light mb-4">
          Vuoi lasciare qui la tua Impronta di oggi?
        </div>

        {/* Nuovo campo facoltativo: "Perché hai lasciato questa Impronta?" */}
        <div className="mb-6 text-left">
          <label
            htmlFor="impronta-note"
            className="block text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2"
          >
            Perché hai lasciato questa Impronta?{" "}
            <span className="normal-case tracking-normal text-white/30">
              (facoltativo)
            </span>
          </label>
          <textarea
            id="impronta-note"
            data-testid="impronta-note"
            value={note || ""}
            onChange={(e) => onNoteChange && onNoteChange(e.target.value.slice(0, 600))}
            rows={3}
            placeholder="Un pensiero, un ricordo, un motivo…"
            className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white/90 placeholder-white/25 focus:border-emerald-400/40 focus:outline-none resize-none font-light leading-relaxed"
          />
          <div className="mt-1 text-[10px] text-white/30 text-right tabular-nums">
            {(note || "").length}/600
          </div>
        </div>

        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            data-testid="impronta-confirm-cancel"
            className="px-5 py-2.5 rounded-full text-white/60 hover:text-white text-sm tracking-wide"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="impronta-confirm-ok"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-black text-sm tracking-wide font-medium transition"
            style={{ backgroundColor: category.color }}
          >
            <Fingerprint className="w-4 h-4" />
            Lascia l&apos;impronta
          </button>
        </div>
      </div>
    </div>
  );
}
